import { useEffect, type ReactNode } from 'react';
import { AlertCircle, CheckCircle2, FolderOpen, Search, ShieldAlert, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Button } from './Button';

/** Guideline §12 — Empty & Feedback States. */

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
  compact = false,
}: {
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
  compact?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        compact ? 'px-6 py-10' : 'px-6 py-16',
        className,
      )}
    >
      <span aria-hidden className="text-muted [&>svg]:h-8 [&>svg]:w-8 [&>svg]:stroke-[1.5]">
        {icon ?? <FolderOpen />}
      </span>
      <h3 className="mt-4 text-card-title text-ink">{title}</h3>
      {description && <p className="mt-1.5 max-w-sm text-sm text-muted">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

/** "No results for X" — distinct from an empty collection, per §12. */
export function NoResultsState({ query, onClear }: { query: string; onClear: () => void }) {
  return (
    <EmptyState
      icon={<Search />}
      title={`No results for "${query}"`}
      description="Try different keywords or check your spelling."
      action={
        <Button variant="outline" onClick={onClear}>
          Clear Search
        </Button>
      }
    />
  );
}

export function AccessRestrictedState() {
  return (
    <EmptyState
      icon={<ShieldAlert className="text-danger" />}
      title="Access restricted"
      description="You don't have permission to view this project. Contact your administrator."
    />
  );
}

/**
 * Form-level error banner — the box above a form when the submission itself
 * failed, as opposed to a single field. Carries the same `AlertCircle` used for
 * field errors in `Field.tsx`, so both levels of error read as the same thing.
 *
 * The icon is `aria-hidden`: `role="alert"` already announces the message, and
 * an announced icon would just add noise.
 */
export function FormAlert({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      role="alert"
      className={cn(
        'flex items-start gap-2.5 rounded-lg border border-danger-200 bg-danger-50 px-4 py-3',
        className,
      )}
    >
      <AlertCircle aria-hidden className="mt-px h-4 w-4 shrink-0 text-danger-600" />
      <p className="min-w-0 flex-1 text-sm font-medium text-danger-600">{children}</p>
    </div>
  );
}

/** Inline success/failure feedback — e.g. after a file upload. */
export function InlineFeedback({
  tone,
  title,
  description,
  action,
  className,
}: {
  tone: 'success' | 'error';
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  const success = tone === 'success';
  return (
    <div className={cn('flex items-start gap-3', className)}>
      <span
        className={cn(
          'mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
          success ? 'bg-signal-50 text-signal' : 'bg-danger-50 text-danger',
        )}
      >
        {success ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
      </span>
      <div className="min-w-0 flex-1">
        <p className={cn('text-sm font-semibold', success ? 'text-signal-700' : 'text-danger-600')}>{title}</p>
        {description && <p className="mt-0.5 text-helper text-muted">{description}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export type ToastTone = 'success' | 'error' | 'info';

export interface ToastMessage {
  id: string;
  tone: ToastTone;
  message: string;
}

export function Toast({ toast, onDismiss }: { toast: ToastMessage; onDismiss: (id: string) => void }) {
  useEffect(() => {
    const timer = window.setTimeout(() => onDismiss(toast.id), 5000);
    return () => window.clearTimeout(timer);
  }, [toast.id, onDismiss]);

  const icons = {
    success: <CheckCircle2 className="h-4 w-4 text-signal" />,
    error: <AlertCircle className="h-4 w-4 text-danger" />,
    info: <AlertCircle className="h-4 w-4 text-digital" />,
  } as const;

  return (
    <div
      role="status"
      className="flex animate-toast-in items-center gap-3 rounded-lg border border-line bg-white px-4 py-3 shadow-raised"
    >
      <span aria-hidden className="shrink-0">
        {icons[toast.tone]}
      </span>
      <p className="flex-1 text-sm text-body">{toast.message}</p>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        aria-label="Dismiss notification"
        className="shrink-0 rounded p-0.5 text-muted transition-colors hover:bg-cloud hover:text-body"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

/** Skeleton block for loading states. Width/height come from the caller. */
export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden className={cn('skeleton rounded', className)} />;
}

export function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <div className={cn('space-y-2.5', className)}>
      {Array.from({ length: lines }).map((_, index) => (
        <Skeleton
          key={index}
          className={cn('h-3', index === lines - 1 ? 'w-2/5' : index % 2 === 0 ? 'w-full' : 'w-4/5')}
        />
      ))}
    </div>
  );
}
